/**
 * CPF digit verification — standard Brazilian algorithm
 * Validates the two check digits of an 11-digit CPF string (digits only).
 */
export function validateCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');

  if (digits.length !== 11) return false;

  // Reject sequences of all identical digits (e.g. 111.111.111-11)
  if (/^(\d)\1{10}$/.test(digits)) return false;

  // First check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i], 10) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(digits[9], 10)) return false;

  // Second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i], 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(digits[10], 10)) return false;

  return true;
}

/**
 * Converts a CNPJ character to its numeric value for check digit calculation.
 * Per Receita Federal specification: value = ASCII code - 48.
 * Digits 0-9 → 0-9 (ASCII 48-57 minus 48); Letters A-Z → 17-42 (ASCII 65-90 minus 48).
 */
function cnpjCharValue(ch: string): number {
  return ch.toUpperCase().charCodeAt(0) - 48;
}

/**
 * CNPJ digit verification — supports the new Brazilian alphanumeric CNPJ format.
 * Accepts both the old all-numeric format and the new format that allows letters
 * in the first 12 characters (positions 1–12). The last 2 characters are always
 * numeric check digits.
 *
 * Input: raw CNPJ string, with or without formatting characters (. / -).
 * The function strips formatting and works on the 14-character alphanumeric string.
 */
export function validateCNPJ(cnpj: string): boolean {
  // Strip only formatting separators (. / - and spaces), keep letters
  const raw = cnpj.replace(/[.\-/\s]/g, '').toUpperCase();

  if (raw.length !== 14) return false;

  // Must be alphanumeric
  if (!/^[A-Z0-9]{14}$/.test(raw)) return false;

  // Reject sequences of all identical characters (e.g. 00000000000000, AAAAAAAAAAAAAA)
  if (/^(.)\1{13}$/.test(raw)) return false;

  // The last two characters must be digits (check digits)
  if (!/^\d$/.test(raw[12]) || !/^\d$/.test(raw[13])) return false;

  /**
   * New check digit algorithm (Receita Federal, 2024 update):
   * weights cycle from 2 to 9 repeatedly, applied right-to-left over the first 12 chars.
   * remainder = sum % 11; digit = remainder < 2 ? 0 : 11 - remainder
   */
  const calcDigit = (slice: string): number => {
    let sum = 0;
    let weight = 2;
    // iterate right-to-left
    for (let i = slice.length - 1; i >= 0; i--) {
      sum += cnpjCharValue(slice[i]) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    let remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calcDigit(raw.slice(0, 12));
  if (firstDigit !== parseInt(raw[12], 10)) return false;

  const secondDigit = calcDigit(raw.slice(0, 13));
  if (secondDigit !== parseInt(raw[13], 10)) return false;

  return true;
}

/**
 * Format a raw 11-digit CPF string into 000.000.000-00 format.
 */
export function formatCPF(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

/**
 * Format a 14-character CNPJ string (alphanumeric) into XX.XXX.XXX/XXXX-DD format.
 */
export function formatCNPJ(raw: string): string {
  // Strip separators but keep letters
  const d = raw.replace(/[.\-/\s]/g, '').toUpperCase().slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}